import React, { useEffect, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LogIn, UserPlus, LogOut, Star, History, Gift, Award, Calendar, ChevronRight, ShoppingBag, Languages, Crown, ShieldCheck, RotateCcw, X, MessageSquare, Edit3 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import SEO from "@/components/SEO";
import ReviewModal from "@/components/ReviewModal";

const TIER_COLORS = {
  Bronze: { bg: "linear-gradient(135deg,#F3D5B5,#C9803C)", text: "#3E1F05" },
  Silver: { bg: "linear-gradient(135deg,#F5F5F5,#C0C0C0)", text: "#2E2E2E" },
  Gold: { bg: "linear-gradient(135deg,#FFE27A,#C9922F)", text: "#5C3D06" },
  Platinum: { bg: "linear-gradient(135deg,#FFFFFF,#8E95A0)", text: "#1E2228" },
};

// Rich metallic themes for the membership hero card — the entire card adopts the tier color
const TIER_HERO_THEMES = {
  Bronze: {
    cardBg: "linear-gradient(135deg, #2a1608 0%, #4a2611 25%, #8b4513 60%, #cd7f32 90%, #6b3410 100%)",
    sheen: "linear-gradient(115deg, transparent 30%, rgba(255,200,140,0.18) 48%, transparent 60%)",
    glow: "radial-gradient(circle, #cd7f32 0%, transparent 70%)",
    border: "rgba(205,127,50,0.35)",
    textPrimary: "#fff5e8",
    textMuted: "rgba(255, 235, 210, 0.7)",
    labelColor: "rgba(255, 220, 180, 0.55)",
    pointsAccent: "#ffd9a8",
    badgeBg: "linear-gradient(135deg,#F3D5B5,#C9803C)",
    badgeText: "#3E1F05",
    progressEnd: "#cd7f32",
  },
  Silver: {
    cardBg: "linear-gradient(135deg, #1f2329 0%, #3d434d 20%, #8a929e 45%, #e2e5ea 60%, #b1b6c0 80%, #4a5060 100%)",
    sheen: "linear-gradient(115deg, transparent 28%, rgba(255,255,255,0.35) 48%, transparent 62%)",
    glow: "radial-gradient(circle, #e8eaed 0%, transparent 70%)",
    border: "rgba(220,224,232,0.45)",
    textPrimary: "#0e1116",
    textMuted: "rgba(20, 24, 30, 0.68)",
    labelColor: "rgba(20, 24, 30, 0.55)",
    pointsAccent: "#1a1f28",
    badgeBg: "linear-gradient(135deg,#FFFFFF,#A8AEB8)",
    badgeText: "#1f242c",
    progressEnd: "#e2e5ea",
  },
  Gold: {
    cardBg: "linear-gradient(135deg, #2b1a04 0%, #5c3d06 18%, #b8893d 42%, #ffd76a 60%, #c9922f 82%, #6b4810 100%)",
    sheen: "linear-gradient(115deg, transparent 28%, rgba(255,243,200,0.4) 48%, transparent 62%)",
    glow: "radial-gradient(circle, #ffe27a 0%, transparent 70%)",
    border: "rgba(255,215,106,0.45)",
    textPrimary: "#1a0f00",
    textMuted: "rgba(40, 22, 0, 0.72)",
    labelColor: "rgba(50, 28, 0, 0.6)",
    pointsAccent: "#3a2200",
    badgeBg: "linear-gradient(135deg,#FFF7D6,#C9922F)",
    badgeText: "#3a2200",
    progressEnd: "#ffd76a",
  },
  Platinum: {
    cardBg: "linear-gradient(135deg, #0d1014 0%, #2c313b 18%, #6e7886 38%, #d8dce2 55%, #ffffff 68%, #a4abb6 88%, #2c313b 100%)",
    sheen: "linear-gradient(115deg, transparent 26%, rgba(255,255,255,0.55) 48%, transparent 62%)",
    glow: "radial-gradient(circle, #ffffff 0%, transparent 70%)",
    border: "rgba(232,236,242,0.55)",
    textPrimary: "#0a0d12",
    textMuted: "rgba(15, 18, 24, 0.7)",
    labelColor: "rgba(15, 18, 24, 0.55)",
    pointsAccent: "#0a0d12",
    badgeBg: "linear-gradient(135deg,#FFFFFF,#8E95A0)",
    badgeText: "#0a0d12",
    progressEnd: "#ffffff",
  },
};

export default function CustomerPortal() {
  const { id: salonId } = useParams();
  const navigate = useNavigate();
  const [lang, setLang] = useState("ar");
  const isRTL = lang === "ar";
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [purchases, setPurchases] = useState({ invoices: [], bookings: [] });
  const [pointsHistory, setPointsHistory] = useState({ history: [], current_balance: 0, tier: "Bronze" });
  const [offers, setOffers] = useState({ personal: [], promotions: [] });
  const [loyaltyProgram, setLoyaltyProgram] = useState(null);
  const [loyaltyHistory, setLoyaltyHistory] = useState({ events: [], current_points: 0, current_tier: "Bronze", total_earned_lifetime: 0, total_redeemed_lifetime: 0 });
  const [invoiceModal, setInvoiceModal] = useState(null);

  // Phase 5 — Reviews state
  const [reviewsTab, setReviewsTab] = useState("eligible"); // eligible | mine
  const [eligibleBookings, setEligibleBookings] = useState([]);
  const [myReviews, setMyReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsFlagOff, setReviewsFlagOff] = useState(false);
  const [reviewModalBooking, setReviewModalBooking] = useState(null);

  // Auth forms
  const [authMode, setAuthMode] = useState("login"); // login | register
  const [authForm, setAuthForm] = useState({ phone: "", password: "", name: "", email: "" });

  // Rebook modal
  const [rebookFor, setRebookFor] = useState(null); // booking object
  const [rebookForm, setRebookForm] = useState({ date: "", time_slot: "", notes: "" });
  const [rebookSubmitting, setRebookSubmitting] = useState(false);

  const openRebook = (b) => {
    setRebookFor(b);
    setRebookForm({ date: "", time_slot: "", notes: "" });
  };
  const closeRebook = () => setRebookFor(null);
  const submitRebook = async () => {
    if (!rebookForm.date || !rebookForm.time_slot) {
      toast.error(isRTL ? "اختر التاريخ والوقت" : "Pick date & time");
      return;
    }
    setRebookSubmitting(true);
    try {
      const r = await api.post("/customer/rebook", {
        booking_id: rebookFor.booking_id,
        date: rebookForm.date,
        time_slot: rebookForm.time_slot,
        notes: rebookForm.notes,
      });
      toast.success(isRTL ? `تم تأكيد الحجز في ${r.data.date} · ${r.data.time_slot}` : `Booked on ${r.data.date} at ${r.data.time_slot}`);
      closeRebook();
      // Refresh purchases
      const p = await api.get("/customer/purchases");
      setPurchases(p.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || (isRTL ? "تعذّر إعادة الحجز" : "Rebook failed"));
    } finally {
      setRebookSubmitting(false);
    }
  };

  useEffect(() => {
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    document.documentElement.lang = lang;
  }, [lang]);

  const checkAuth = async () => {
    setLoading(true);
    try {
      const me = await api.get("/customer/me");
      setCustomer(me.data);
      const [p, h, o, lp, lh] = await Promise.all([
        api.get("/customer/purchases"),
        api.get("/customer/points-history").catch(() => ({ data: { history: [], current_balance: 0, tier: "Bronze" } })),
        api.get("/customer/offers").catch(() => ({ data: { personal: [], promotions: [] } })),
        api.get("/customer/loyalty-program"),
        api.get("/customer/loyalty-history"),
      ]);
      setPurchases(p.data);
      setPointsHistory(h.data);
      setOffers(o.data);
      setLoyaltyProgram(lp.data);
      setLoyaltyHistory(lh.data);
    } catch {
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { checkAuth(); }, []);

  // Phase 5 — fetch reviews data when the tab is opened
  const loadReviews = async () => {
    setReviewsLoading(true);
    setReviewsFlagOff(false);
    try {
      const [a, b] = await Promise.all([
        api.get("/marketplace/reviews/customer/eligible"),
        api.get("/marketplace/reviews/customer/mine"),
      ]);
      setEligibleBookings(a.data.items || []);
      setMyReviews(b.data.items || []);
    } catch (e) {
      if (e?.response?.status === 503) {
        setReviewsFlagOff(true);
      }
      setEligibleBookings([]);
      setMyReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "reviews" && customer) loadReviews();
    // eslint-disable-next-line
  }, [tab, customer]);

  const submitAuth = async () => {
    if (!authForm.phone || !authForm.password) return toast.error(isRTL ? "أدخل البيانات" : "Fill required fields");
    if (authMode === "register" && !authForm.name) return toast.error(isRTL ? "الاسم مطلوب" : "Name required");
    try {
      const url = authMode === "login" ? "/customer/login" : "/customer/register";
      await api.post(url, authForm);
      toast.success(isRTL ? "أهلاً بك!" : "Welcome!");
      checkAuth();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const logout = async () => {
    await api.post("/customer/logout");
    setCustomer(null);
    toast.success(isRTL ? "تم تسجيل الخروج" : "Logged out");
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FBF5F1]">{isRTL ? "تحميل..." : "Loading..."}</div>;

  // ============ AUTH SCREEN ============
  if (!customer) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FBF5F1] via-white to-[#FBF5F1] flex items-center justify-center p-4" data-testid="customer-auth-page">
        <SEO title={lang === "ar" ? "بوابة العميل | iSaloon" : "Customer Portal | iSaloon"} noindex={true} lang={lang} />
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <Link to={`/salon/${salonId || "default"}`} className="inline-block">
              <div className="font-heading text-4xl text-[#7A1F3D]">iSaloon</div>
              <div className="text-[10px] tracking-[0.3em] text-stone-500 mt-1">BEAUTY &amp; CARE</div>
            </Link>
          </div>
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-stone-100">
            <div className="flex justify-end mb-2">
              <button onClick={() => setLang(lang === "ar" ? "en" : "ar")} className="text-xs px-2 py-1 border border-stone-200 rounded hover:border-[#7A1F3D] transition flex items-center gap-1"><Languages size={12} />{lang === "ar" ? "EN" : "ع"}</button>
            </div>
            <h1 className="text-2xl font-bold text-stone-800">{authMode === "login" ? (isRTL ? "تسجيل الدخول" : "Sign In") : (isRTL ? "إنشاء حساب" : "Create Account")}</h1>
            <p className="text-sm text-stone-500 mt-1">{isRTL ? "تابع مشترياتك ونقاطك وعروضك المخصصة" : "Track purchases, points and personalized offers"}</p>

            <div className="space-y-3 mt-5">
              {authMode === "register" && (
                <input data-testid="cust-register-name" placeholder={isRTL ? "الاسم الكامل" : "Full name"} className="w-full px-3 py-2.5 border border-stone-200 rounded-md bg-white text-stone-900 placeholder:text-stone-400 focus:border-[#7A1F3D] outline-none" value={authForm.name} onChange={(e) => setAuthForm({ ...authForm, name: e.target.value })} />
              )}
              <input data-testid="cust-auth-phone" placeholder={isRTL ? "رقم الجوال (+974 5XXX XXXX)" : "Phone (+974 5XXX XXXX)"} dir="ltr" className="w-full px-3 py-2.5 border border-stone-200 rounded-md bg-white text-stone-900 placeholder:text-stone-400 focus:border-[#7A1F3D] outline-none" value={authForm.phone} onChange={(e) => setAuthForm({ ...authForm, phone: e.target.value })} />
              <input type="password" data-testid="cust-auth-password" placeholder={isRTL ? "كلمة المرور" : "Password"} className="w-full px-3 py-2.5 border border-stone-200 rounded-md bg-white text-stone-900 placeholder:text-stone-400 focus:border-[#7A1F3D] outline-none" value={authForm.password} onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })} />
              {authMode === "register" && (
                <input data-testid="cust-register-email" placeholder={isRTL ? "البريد (اختياري)" : "Email (optional)"} className="w-full px-3 py-2.5 border border-stone-200 rounded-md bg-white text-stone-900 placeholder:text-stone-400 focus:border-[#7A1F3D] outline-none" value={authForm.email} onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })} />
              )}
              <button onClick={submitAuth} data-testid="cust-auth-submit" className="w-full py-2.5 bg-gradient-to-r from-[#7A1F3D] to-[#9B2950] text-white rounded-md font-semibold hover:opacity-90 transition flex items-center justify-center gap-2">
                {authMode === "login" ? <><LogIn size={16} /> {isRTL ? "دخول" : "Sign In"}</> : <><UserPlus size={16} /> {isRTL ? "إنشاء حساب" : "Create Account"}</>}
              </button>
              <div className="text-center text-sm text-stone-500">
                {authMode === "login" ? (
                  <>{isRTL ? "ليس لديك حساب؟" : "No account?"} <button data-testid="cust-switch-register" onClick={() => setAuthMode("register")} className="text-[#7A1F3D] hover:underline font-semibold">{isRTL ? "أنشئ حساب" : "Sign up"}</button></>
                ) : (
                  <>{isRTL ? "لديك حساب؟" : "Have an account?"} <button data-testid="cust-switch-login" onClick={() => setAuthMode("login")} className="text-[#7A1F3D] hover:underline font-semibold">{isRTL ? "تسجيل الدخول" : "Sign in"}</button></>
                )}
              </div>
            </div>
            <Link to={`/salon/${salonId || "default"}`} className="block text-center text-xs text-stone-400 mt-6 hover:text-[#7A1F3D]">
              <ChevronRight size={12} className={`inline ${isRTL ? "rotate-180" : ""}`} /> {isRTL ? "العودة للموقع" : "Back to site"}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ============ PORTAL ============
  const tier = customer.membership_tier || "Bronze";
  const tierStyle = TIER_COLORS[tier] || TIER_COLORS.Bronze;
  const heroTheme = TIER_HERO_THEMES[tier] || TIER_HERO_THEMES.Bronze;

  return (
    <div className="min-h-screen bg-[#FBF5F1]" data-testid="customer-portal">
      <SEO title={lang === "ar" ? "حسابي | iSaloon" : "My Account | iSaloon"} noindex={true} lang={lang} />
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to={`/salon/${salonId || "default"}`} className="flex items-center gap-3">
            <div>
              <div className="font-heading text-xl text-[#7A1F3D] leading-none">iSaloon</div>
              <div className="text-[9px] tracking-widest text-stone-400 mt-0.5">BEAUTY &amp; CARE</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={() => setLang(lang === "ar" ? "en" : "ar")} className="text-xs px-2 py-1.5 border border-stone-200 rounded hover:border-[#7A1F3D] transition flex items-center gap-1"><Languages size={12} />{lang === "ar" ? "EN" : "ع"}</button>
            <button onClick={logout} data-testid="customer-logout-btn" className="text-xs px-3 py-1.5 border border-rose-200 text-rose-600 rounded hover:bg-rose-50 flex items-center gap-1.5"><LogOut size={12} />{isRTL ? "خروج" : "Logout"}</button>
          </div>
        </div>
      </header>

      {/* Membership Card Hero */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div
          className="rounded-3xl p-6 md:p-8 relative overflow-hidden shadow-2xl border"
          style={{
            background: heroTheme.cardBg,
            color: heroTheme.textPrimary,
            borderColor: heroTheme.border,
            boxShadow: `0 25px 60px -15px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.18)`,
          }}
          data-testid="membership-card-hero"
        >
          {/* Metallic sheen overlay */}
          <div className="absolute inset-0 pointer-events-none" style={{ background: heroTheme.sheen, mixBlendMode: "overlay" }}></div>
          {/* Decorative tier glow */}
          <div className="absolute -top-20 -end-20 w-80 h-80 rounded-full opacity-40 blur-3xl pointer-events-none" style={{ background: heroTheme.glow }}></div>
          <div className="absolute -bottom-24 -start-24 w-64 h-64 rounded-full opacity-25 blur-3xl pointer-events-none" style={{ background: heroTheme.glow }}></div>

          <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-stretch">
            {/* Left side */}
            <div className="flex flex-col justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: heroTheme.labelColor }}>{isRTL ? "بطاقة العضوية" : "Membership Card"}</div>
                <h2 className="text-2xl md:text-3xl font-bold mt-1.5 leading-tight" style={{ color: heroTheme.textPrimary }}>{customer.name}</h2>
                <div className="text-xs mt-1" dir="ltr" style={{ color: heroTheme.textMuted }}>{customer.phone}</div>

                <div className="mt-5 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full font-bold text-sm shadow-lg" style={{ background: heroTheme.badgeBg, color: heroTheme.badgeText, border: `1px solid ${heroTheme.border}` }} data-testid="membership-tier-badge">
                  {tier === "Gold" || tier === "Platinum" ? <Crown size={13} /> : <Award size={13}/>}
                  {isRTL && loyaltyProgram ? (loyaltyProgram.tiers.find(t => t.name === tier)?.name_ar || tier) : tier.toUpperCase()}
                </div>
              </div>

              {/* Points + value */}
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest" style={{ color: heroTheme.labelColor }}>{isRTL ? "النقاط الحالية" : "Current Points"}</div>
                  <div className="text-3xl md:text-4xl font-bold mt-1" style={{ color: heroTheme.pointsAccent }} data-testid="membership-points">{customer.loyalty_points || 0}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest" style={{ color: heroTheme.labelColor }}>{isRTL ? "تساوي" : "Worth"}</div>
                  <div className="text-3xl md:text-4xl font-bold mt-1" style={{ color: heroTheme.pointsAccent }}>{(customer.points_value_qar ?? ((customer.loyalty_points || 0) / 30)).toFixed(2)}<span className="text-sm ms-1" style={{ color: heroTheme.textMuted }}>{isRTL ? "ر.ق" : "QAR"}</span></div>
                </div>
              </div>

              {/* Tier progress */}
              {customer.next_tier && customer.next_tier_target && (
                <div className="mt-6">
                  <div className="flex items-center justify-between text-[11px] mb-1.5">
                    <span style={{ color: heroTheme.textMuted }}>{isRTL ? "التقدّم نحو" : "Progress to"} <strong style={{ color: heroTheme.textPrimary }}>{isRTL && loyaltyProgram ? (loyaltyProgram.tiers.find(t => t.name === customer.next_tier)?.name_ar || customer.next_tier) : customer.next_tier}</strong></span>
                    <span style={{ color: heroTheme.textMuted }}>
                      {isRTL ? `يتبقى ${customer.points_to_next} نقطة` : `${customer.points_to_next} pts to go`}
                    </span>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(0,0,0,0.18)" }} data-testid="tier-progress">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{
                        width: `${Math.min(100, Math.round(((customer.loyalty_points || 0) / customer.next_tier_target) * 100))}%`,
                        background: `linear-gradient(90deg, ${heroTheme.progressEnd}, #9B2950)`,
                      }}
                    />
                  </div>
                </div>
              )}
              {!customer.next_tier && (
                <div className="mt-6 text-xs flex items-center gap-2" style={{ color: heroTheme.textMuted }}>
                  <Crown size={12} style={{ color: heroTheme.pointsAccent }}/>{isRTL ? "وصلت إلى أعلى مستوى — استمتع بكل المزايا الحصرية!" : "You've reached the top tier — enjoy all VIP perks!"}
                </div>
              )}
            </div>

            {/* QR */}
            <div className="flex md:flex-col items-center md:items-end gap-3 md:justify-between">
              <Link to={`/card/${customer.customer_id}`} className="bg-white p-2.5 rounded-xl shadow-xl hover:scale-105 transition" title={isRTL ? "بطاقة العضوية" : "Membership Card"}>
                <QRCodeSVG value={`${window.location.origin}/card/${customer.customer_id}`} size={110} />
              </Link>
              <div className="text-[10px] md:text-end max-w-[110px] leading-tight" style={{ color: heroTheme.textMuted }}>
                {isRTL ? "اعرض هذا الرمز في الصالون لتجميع/استبدال النقاط" : "Show this at the salon to earn / redeem"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-5xl mx-auto px-4 mb-4">
        <div className="flex gap-2 overflow-x-auto bg-white rounded-xl p-1 shadow-sm border border-stone-100">
          {[
            { id: "overview", labelEn: "Overview", labelAr: "نظرة عامة", icon: Award },
            { id: "program", labelEn: "Loyalty Program", labelAr: "برنامج النقاط", icon: Star },
            { id: "purchases", labelEn: "Invoices", labelAr: "الفواتير", icon: ShoppingBag },
            { id: "points", labelEn: "Points History", labelAr: "حركة النقاط", icon: History },
            { id: "reviews", labelEn: "My Reviews", labelAr: "مراجعاتي", icon: MessageSquare },
            { id: "offers", labelEn: "Offers", labelAr: "العروض", icon: Gift },
          ].map((tt) => (
            <button key={tt.id} onClick={() => setTab(tt.id)} data-testid={`portal-tab-${tt.id}`} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition ${tab === tt.id ? "bg-gradient-to-r from-[#7A1F3D] to-[#9B2950] text-white" : "text-stone-600 hover:bg-stone-50"}`}>
              <tt.icon size={14} /> {isRTL ? tt.labelAr : tt.labelEn}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="max-w-5xl mx-auto px-4 pb-12">
        {tab === "overview" && (
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl p-5 border border-stone-100 shadow-sm">
              <ShoppingBag className="text-[#7A1F3D] mb-2" />
              <div className="text-3xl font-bold text-stone-800">{purchases.invoices.length}</div>
              <div className="text-sm text-stone-500">{isRTL ? "إجمالي الفواتير" : "Total Invoices"}</div>
            </div>
            <div className="bg-white rounded-xl p-5 border border-stone-100 shadow-sm">
              <Calendar className="text-[#9B2950] mb-2" />
              <div className="text-3xl font-bold text-stone-800">{purchases.bookings.length}</div>
              <div className="text-sm text-stone-500">{isRTL ? "إجمالي الحجوزات" : "Total Bookings"}</div>
            </div>
            <div className="bg-white rounded-xl p-5 border border-stone-100 shadow-sm">
              <Star className="text-amber-500 mb-2" />
              <div className="text-3xl font-bold text-stone-800">{(customer.total_spent || 0).toFixed(0)}</div>
              <div className="text-sm text-stone-500">{isRTL ? "إجمالي الإنفاق (ر.ق)" : "Total Spent (QAR)"}</div>
            </div>
          </div>
        )}

        {tab === "purchases" && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-stone-100 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-stone-100 font-semibold text-stone-700">{isRTL ? "الفواتير" : "Invoices"} ({purchases.invoices.length})</div>
              <div className="divide-y divide-stone-100">
                {purchases.invoices.map((inv) => {
                  const cancelled = inv.status === "cancelled";
                  return (
                  <button
                    type="button"
                    key={inv.invoice_id}
                    onClick={() => setInvoiceModal(inv)}
                    className="w-full px-5 py-3 flex items-center justify-between text-start hover:bg-stone-50 transition"
                    data-testid={`portal-invoice-${inv.invoice_id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-mono font-semibold text-stone-700">#{inv.invoice_number || inv.invoice_id}</span>
                        {cancelled && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">{isRTL ? "ملغاة" : "VOID"}</span>}
                      </div>
                      <div className="text-xs text-stone-500 mt-0.5">{(inv.created_at || "").slice(0, 10)} · {(inv.items || []).length} {isRTL ? "عنصر" : "items"} · {inv.payment_method}</div>
                    </div>
                    <div className="text-end shrink-0">
                      <div className={`font-bold ${cancelled ? "text-stone-400 line-through" : "text-[#7A1F3D]"}`}>{(inv.total || 0).toFixed(2)} {isRTL ? "ر.ق" : "QAR"}</div>
                      {!cancelled && inv.loyalty_earned > 0 && <div className="text-xs text-green-600">+{inv.loyalty_earned} {isRTL ? "نقطة" : "pts"}</div>}
                    </div>
                  </button>
                  );
                })}
                {purchases.invoices.length === 0 && <div className="text-center text-stone-400 py-8 text-sm">{isRTL ? "لا توجد فواتير" : "No invoices yet"}</div>}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-stone-100 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-stone-100 font-semibold text-stone-700">{isRTL ? "الحجوزات" : "Bookings"} ({purchases.bookings.length})</div>
              <div className="divide-y divide-stone-100">
                {purchases.bookings.map((b) => (
                  <div key={b.booking_id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`portal-booking-${b.booking_id}`}>
                    <div className="min-w-0">
                      <div className="text-sm text-stone-700 truncate">{isRTL ? b.item_name_ar : b.item_name_en}</div>
                      <div className="text-xs text-stone-500 mt-0.5">{b.date} · {b.time_slot || b.time}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => openRebook(b)}
                        data-testid={`rebook-btn-${b.booking_id}`}
                        className="text-xs px-2.5 py-1 rounded-full border border-[#7A1F3D] text-[#7A1F3D] hover:bg-[#7A1F3D] hover:text-white transition flex items-center gap-1"
                      >
                        <RotateCcw size={11}/>{isRTL ? "احجز مجدداً" : "Rebook"}
                      </button>
                      <span className={`text-xs px-2 py-1 rounded-full ${b.status === "completed" ? "bg-green-100 text-green-700" : b.status === "cancelled" ? "bg-rose-100 text-rose-700" : "bg-blue-100 text-blue-700"}`}>{b.status}</span>
                    </div>
                  </div>
                ))}
                {purchases.bookings.length === 0 && <div className="text-center text-stone-400 py-8 text-sm">{isRTL ? "لا توجد حجوزات" : "No bookings yet"}</div>}
              </div>
            </div>
          </div>
        )}

        {tab === "program" && loyaltyProgram && (
          <div className="space-y-4" data-testid="loyalty-program-tab">
            {/* Earn + redeem rates */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl p-6 border border-stone-100 shadow-sm">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-3"><Star className="text-green-600" /></div>
                <h3 className="font-bold text-lg text-stone-800">{isRTL ? "كيف تكسب النقاط؟" : "How to earn"}</h3>
                <div className="mt-3 px-4 py-3 bg-green-50 rounded-lg border border-green-100">
                  <div className="text-3xl font-bold text-green-700">1 <span className="text-sm font-normal">{isRTL ? "ر.ق" : "QAR"}</span> = 1 <span className="text-sm font-normal">{isRTL ? "نقطة" : "pt"}</span></div>
                </div>
                <p className="text-sm text-stone-600 mt-3 leading-relaxed">{isRTL ? loyaltyProgram.earn_rate.description_ar : loyaltyProgram.earn_rate.description_en}</p>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-stone-100 shadow-sm">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mb-3"><Gift className="text-amber-600" /></div>
                <h3 className="font-bold text-lg text-stone-800">{isRTL ? "كيف تستبدل النقاط؟" : "How to redeem"}</h3>
                <div className="mt-3 px-4 py-3 bg-amber-50 rounded-lg border border-amber-100">
                  <div className="text-3xl font-bold text-amber-700">{loyaltyProgram.redeem_rate.points_per_qar} <span className="text-sm font-normal">{isRTL ? "نقطة" : "pts"}</span> = 1 <span className="text-sm font-normal">{isRTL ? "ر.ق" : "QAR"}</span></div>
                </div>
                <p className="text-sm text-stone-600 mt-3 leading-relaxed">{isRTL ? loyaltyProgram.redeem_rate.description_ar : loyaltyProgram.redeem_rate.description_en}</p>
              </div>
            </div>

            {/* Tiers ladder */}
            <div className="bg-white rounded-2xl p-6 border border-stone-100 shadow-sm">
              <h3 className="font-bold text-lg text-stone-800 mb-4 flex items-center gap-2"><Crown className="text-[#7A1F3D]"/>{isRTL ? "مستويات العضوية" : "Membership Tiers"}</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                {loyaltyProgram.tiers.map((t) => {
                  const isCurrent = customer.membership_tier === t.name;
                  const isNext = customer.next_tier === t.name;
                  return (
                    <div
                      key={t.name}
                      className={`rounded-xl p-4 border-2 transition relative ${isCurrent ? "border-[#7A1F3D] bg-[#FBF5F1] shadow-md" : isNext ? "border-amber-300 bg-amber-50/60" : "border-stone-200 bg-stone-50/50"}`}
                      data-testid={`tier-${t.name.toLowerCase()}`}
                    >
                      {isCurrent && <span className="absolute -top-2 end-3 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#7A1F3D] text-white shadow">{isRTL ? "حالي" : "CURRENT"}</span>}
                      {isNext && !isCurrent && <span className="absolute -top-2 end-3 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500 text-white shadow">{isRTL ? "التالي" : "NEXT"}</span>}
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-full" style={{ background: t.color }}></div>
                        <div>
                          <div className="font-bold text-stone-800">{isRTL ? t.name_ar : t.name}</div>
                          <div className="text-[10px] text-stone-500">{t.min_points}+ {isRTL ? "نقطة" : "pts"}</div>
                        </div>
                      </div>
                      <ul className="text-xs text-stone-600 space-y-1 mt-3">
                        {(isRTL ? t.perks_ar : t.perks_en).map((p, idx) => (
                          <li key={idx} className="flex items-start gap-1.5"><span className="text-green-500 mt-0.5">✓</span><span>{p}</span></li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-gradient-to-r from-[#FBF5F1] to-amber-50 rounded-xl p-4 border border-amber-100 text-sm text-stone-700">
              <strong>{isRTL ? "ملاحظة:" : "Note:"}</strong> {isRTL ? "النقاط تُحتسب على الفواتير المدفوعة فقط، وتُسترد تلقائياً عند إلغاء أي فاتورة." : "Points are credited on paid invoices and auto-reversed on cancellations."}
            </div>
          </div>
        )}

        {tab === "points" && (
          <div className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-5 border border-stone-100 shadow-sm">
                <div className="text-xs text-stone-500 uppercase tracking-wide">{isRTL ? "الرصيد الحالي" : "Current Balance"}</div>
                <div className="text-4xl font-bold text-[#7A1F3D] mt-1" data-testid="loyalty-current-balance">{loyaltyHistory.current_points}</div>
                <div className="text-xs text-stone-500 mt-1">{isRTL ? "نقطة" : "pts"} · ≈ {((loyaltyHistory.current_points || 0) / 30).toFixed(2)} {isRTL ? "ر.ق" : "QAR"}</div>
              </div>
              <div className="bg-white rounded-xl p-5 border border-stone-100 shadow-sm">
                <div className="text-xs text-stone-500 uppercase tracking-wide">{isRTL ? "إجمالي المكتسب" : "Lifetime Earned"}</div>
                <div className="text-4xl font-bold text-green-600 mt-1">+{loyaltyHistory.total_earned_lifetime}</div>
                <div className="text-xs text-stone-500 mt-1">{isRTL ? "نقطة منذ الانضمام" : "since joining"}</div>
              </div>
              <div className="bg-white rounded-xl p-5 border border-stone-100 shadow-sm">
                <div className="text-xs text-stone-500 uppercase tracking-wide">{isRTL ? "إجمالي المستبدل" : "Lifetime Redeemed"}</div>
                <div className="text-4xl font-bold text-amber-600 mt-1">−{loyaltyHistory.total_redeemed_lifetime}</div>
                <div className="text-xs text-stone-500 mt-1">≈ {((loyaltyHistory.total_redeemed_lifetime || 0) / 30).toFixed(2)} {isRTL ? "ر.ق" : "QAR"}</div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-stone-100 shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-stone-100 font-semibold text-stone-700 flex items-center justify-between">
                <span>{isRTL ? "حركة النقاط" : "Points Timeline"}</span>
                <span className="text-xs text-stone-400 font-normal">{loyaltyHistory.events.length} {isRTL ? "حركة" : "movement(s)"}</span>
              </div>
              <div className="divide-y divide-stone-100" data-testid="loyalty-history-list">
                {loyaltyHistory.events.map((ev, idx) => {
                  const positive = ev.delta > 0;
                  const colorBg = positive ? "bg-green-100" : "bg-rose-100";
                  const colorText = positive ? "text-green-700" : "text-rose-700";
                  return (
                    <div key={idx} className="px-5 py-3 flex items-center gap-4" data-testid={`loyalty-event-${idx}`}>
                      <div className={`w-10 h-10 rounded-full ${colorBg} flex items-center justify-center shrink-0`}>
                        {ev.kind === "earn" && <Star className="text-green-700" size={16}/>}
                        {ev.kind === "redeem" && <Gift className="text-rose-700" size={16}/>}
                        {(ev.kind === "void_reverse_earn" || ev.kind === "void_restore_redeem") && <RotateCcw className={positive ? "text-green-700" : "text-rose-700"} size={16}/>}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className={`text-sm font-medium ${ev.voided ? "text-stone-500 italic" : "text-stone-800"}`}>{isRTL ? ev.label_ar : ev.label_en}</div>
                        <div className="text-xs text-stone-500 mt-0.5">{(ev.at || "").slice(0, 16).replace("T", " ")}{ev.invoice_total ? ` · ${(ev.invoice_total).toFixed(2)} ${isRTL ? "ر.ق" : "QAR"}` : ""}</div>
                      </div>
                      <div className="text-end shrink-0">
                        <div className={`font-bold text-lg ${colorText}`}>{positive ? "+" : ""}{ev.delta}</div>
                        <div className="text-[10px] text-stone-400">{isRTL ? "الرصيد" : "bal"} {ev.balance_after}</div>
                      </div>
                    </div>
                  );
                })}
                {loyaltyHistory.events.length === 0 && <div className="text-center text-stone-400 py-12 text-sm">{isRTL ? "لا توجد حركات بعد. ابدأ بحجز خدمتك الأولى!" : "No movements yet. Book your first service!"}</div>}
              </div>
            </div>
          </div>
        )}

        {tab === "reviews" && (
          <div className="space-y-4" data-testid="reviews-tab-pane">
            {reviewsFlagOff && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800" data-testid="reviews-flag-off">
                {isRTL
                  ? "ميزة المراجعات غير مفعّلة حالياً. سيتم تفعيلها قريباً."
                  : "Reviews are not enabled yet. Coming soon."}
              </div>
            )}

            {!reviewsFlagOff && (
              <div className="flex gap-2" data-testid="reviews-sub-tabs">
                <button onClick={() => setReviewsTab("eligible")} data-testid="reviews-subtab-eligible" className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${reviewsTab === "eligible" ? "bg-[#1a1015] text-white border-[#1a1015]" : "bg-white text-stone-700 border-stone-200"}`}>
                  {isRTL ? `بانتظار تقييمك (${eligibleBookings.length})` : `Awaiting your review (${eligibleBookings.length})`}
                </button>
                <button onClick={() => setReviewsTab("mine")} data-testid="reviews-subtab-mine" className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${reviewsTab === "mine" ? "bg-[#1a1015] text-white border-[#1a1015]" : "bg-white text-stone-700 border-stone-200"}`}>
                  {isRTL ? `مراجعاتي (${myReviews.length})` : `My reviews (${myReviews.length})`}
                </button>
              </div>
            )}

            {!reviewsFlagOff && reviewsLoading && (
              <div className="space-y-3" data-testid="reviews-skeleton">
                {[1, 2].map((i) => (
                  <div key={i} className="bg-white rounded-xl p-4 border border-stone-100 animate-pulse">
                    <div className="h-4 bg-stone-200 rounded w-1/3 mb-2" />
                    <div className="h-3 bg-stone-100 rounded w-1/2" />
                  </div>
                ))}
              </div>
            )}

            {!reviewsLoading && !reviewsFlagOff && reviewsTab === "eligible" && (
              <div className="bg-white rounded-xl border border-stone-100 divide-y divide-stone-100">
                {eligibleBookings.length === 0 ? (
                  <div className="p-8 text-center text-stone-400 text-sm">
                    {isRTL ? "لا توجد حجوزات تنتظر تقييمك حالياً." : "No bookings awaiting your review."}
                  </div>
                ) : (
                  eligibleBookings.map((b) => (
                    <div key={b.booking_id} className="p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`eligible-row-${b.booking_id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-stone-800">
                          {(b.listing_name && (isRTL ? (b.listing_name.ar || b.listing_name.en) : (b.listing_name.en || b.listing_name.ar))) || (isRTL ? "صالون" : "Salon")}
                        </div>
                        <div className="text-xs text-stone-500 mt-0.5">
                          {b.service_name && <span>{b.service_name} · </span>}
                          {b.date}
                        </div>
                      </div>
                      <button
                        onClick={() => setReviewModalBooking(b)}
                        data-testid={`write-review-${b.booking_id}`}
                        className="text-xs px-3 py-1.5 rounded-full bg-gradient-to-r from-[#7A1F3D] to-[#9B2950] text-white inline-flex items-center gap-1.5"
                      >
                        <Edit3 size={11} /> {isRTL ? "اكتب مراجعة" : "Write a review"}
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}

            {!reviewsLoading && !reviewsFlagOff && reviewsTab === "mine" && (
              <div className="bg-white rounded-xl border border-stone-100 divide-y divide-stone-100">
                {myReviews.length === 0 ? (
                  <div className="p-8 text-center text-stone-400 text-sm">
                    {isRTL ? "لم تكتب أي مراجعة بعد." : "You haven't written any reviews yet."}
                  </div>
                ) : (
                  myReviews.map((r) => {
                    const statusColor = r.status === "approved" ? "bg-emerald-100 text-emerald-700"
                      : r.status === "pending" ? "bg-amber-100 text-amber-700"
                      : r.status === "rejected" ? "bg-rose-100 text-rose-700"
                      : "bg-stone-100 text-stone-600";
                    const statusLabel = r.status === "approved" ? (isRTL ? "منشورة" : "Published")
                      : r.status === "pending" ? (isRTL ? "قيد المراجعة" : "Pending")
                      : r.status === "rejected" ? (isRTL ? "مرفوضة" : "Rejected")
                      : (isRTL ? "مخفية" : "Hidden");
                    return (
                      <div key={r.review_id} className="p-4" data-testid={`my-review-${r.review_id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-stone-800 text-sm">
                              {(r.listing_name && (isRTL ? (r.listing_name.ar || r.listing_name.en) : (r.listing_name.en || r.listing_name.ar))) || r.listing_id}
                            </div>
                            <div className="flex items-center gap-0.5 mt-1">
                              {[1, 2, 3, 4, 5].map((n) => (
                                <Star key={n} size={13} className={n <= (r.ratings?.overall || 0) ? "fill-amber-400 text-amber-400" : "text-stone-300"} />
                              ))}
                              <span className="text-[10px] text-stone-400 ms-1">{(r.ts || "").slice(0, 10)}</span>
                            </div>
                          </div>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${statusColor}`}>{statusLabel}</span>
                        </div>
                        {(r.text?.ar || r.text?.en) && (
                          <p className="text-sm text-stone-600 mt-2 leading-relaxed" dir="auto">
                            {r.text?.ar || r.text?.en}
                          </p>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        )}

        {tab === "offers" && (
          <div className="space-y-4">
            {offers.personal.length > 0 && (
              <div>
                <h3 className="font-semibold text-stone-700 mb-3 flex items-center gap-2"><Gift size={16} /> {isRTL ? "عروض مخصصة لك" : "Personalized Offers"}</h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {offers.personal.map((o) => (
                    <div key={o.offer_id} className="bg-gradient-to-br from-[#FFF4C4] to-[#FCE5C8] rounded-xl p-5 border border-amber-200" data-testid={`personal-offer-${o.offer_id}`}>
                      <div className="flex items-start justify-between">
                        <h4 className="font-bold text-amber-900 text-lg">{isRTL ? o.title_ar : (o.title_en || o.title_ar)}</h4>
                        {o.discount_percent > 0 && <span className="bg-amber-900 text-amber-100 px-2 py-0.5 rounded text-xs font-bold">{o.discount_percent}%</span>}
                      </div>
                      <p className="text-sm text-amber-800/80 mt-1">{isRTL ? o.description_ar : (o.description_en || o.description_ar)}</p>
                      {o.code && <div className="mt-3 px-3 py-2 bg-white/60 rounded font-mono font-bold text-amber-900 text-center tracking-wider">{o.code}</div>}
                      {o.valid_until && <div className="text-[11px] text-amber-700/70 mt-2">{isRTL ? "صالح حتى" : "Valid until"} {o.valid_until}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {offers.promotions.length > 0 && (
              <div>
                <h3 className="font-semibold text-stone-700 mb-3 flex items-center gap-2"><Star size={16} /> {isRTL ? "عروض عامة" : "General Promotions"}</h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {offers.promotions.map((p) => (
                    <div key={p.coupon_id} className="bg-white rounded-xl p-5 border-2 border-dashed border-[#7A1F3D]/30">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-bold text-stone-800">{isRTL ? p.name_ar : p.name_en}</h4>
                          <p className="text-xs text-stone-500 mt-0.5">{p.description}</p>
                        </div>
                        <span className="bg-[#7A1F3D] text-white px-2 py-1 rounded text-xs font-bold">{p.type === "percent" ? `${p.value}%` : `${p.value} QAR`}</span>
                      </div>
                      <div className="mt-3 px-3 py-2 bg-stone-50 rounded font-mono font-bold text-[#7A1F3D] text-center tracking-wider">{p.code}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {offers.personal.length === 0 && offers.promotions.length === 0 && (
              <div className="text-center text-stone-400 py-12 bg-white rounded-xl border border-stone-100">
                <Gift className="mx-auto mb-2 opacity-40" size={40} />
                {isRTL ? "لا توجد عروض حالياً. تابعنا قريباً!" : "No offers right now. Stay tuned!"}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Rebook modal */}
      {rebookFor && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, closeRebook)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onMouseDown={onModalPanelMouseDown} data-testid="rebook-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-stone-800 flex items-center gap-2"><RotateCcw size={18} className="text-[#7A1F3D]"/>{isRTL ? "إعادة حجز" : "Rebook"}</h3>
              <button onClick={closeRebook} className="text-stone-400 hover:text-stone-700"><X size={18}/></button>
            </div>
            <div className="bg-stone-50 rounded-md p-3 mb-4 text-sm">
              <div className="font-semibold text-stone-800">{isRTL ? rebookFor.item_name_ar : rebookFor.item_name_en}</div>
              <div className="text-xs text-stone-500 mt-1">{isRTL ? "آخر حجز:" : "Last booked:"} {rebookFor.date} · {rebookFor.time_slot || rebookFor.time}</div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-stone-500 block mb-1">{isRTL ? "التاريخ" : "Date"}</label>
                <input
                  type="date"
                  value={rebookForm.date}
                  min={new Date().toISOString().slice(0,10)}
                  onChange={e => setRebookForm({ ...rebookForm, date: e.target.value })}
                  data-testid="rebook-date-input"
                  className="w-full px-3 py-2 border-2 border-stone-300 rounded-md text-stone-900 focus:outline-none focus:border-[#7A1F3D]"
                />
              </div>
              <div>
                <label className="text-xs text-stone-500 block mb-1">{isRTL ? "الوقت" : "Time"}</label>
                <input
                  type="time"
                  value={rebookForm.time_slot}
                  onChange={e => setRebookForm({ ...rebookForm, time_slot: e.target.value })}
                  data-testid="rebook-time-input"
                  className="w-full px-3 py-2 border-2 border-stone-300 rounded-md text-stone-900 focus:outline-none focus:border-[#7A1F3D]"
                />
              </div>
              <div>
                <label className="text-xs text-stone-500 block mb-1">{isRTL ? "ملاحظات (اختياري)" : "Notes (optional)"}</label>
                <textarea
                  rows={2}
                  value={rebookForm.notes}
                  onChange={e => setRebookForm({ ...rebookForm, notes: e.target.value })}
                  className="w-full px-3 py-2 border-2 border-stone-300 rounded-md text-stone-900 focus:outline-none focus:border-[#7A1F3D]"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={closeRebook} className="flex-1 px-4 py-2 border border-stone-300 rounded-md text-stone-600 hover:bg-stone-50 transition text-sm">{isRTL ? "إلغاء" : "Cancel"}</button>
              <button
                onClick={submitRebook}
                disabled={rebookSubmitting}
                data-testid="rebook-submit"
                className="flex-1 px-4 py-2 bg-[#7A1F3D] text-white rounded-md hover:bg-[#9C7430] transition text-sm font-semibold disabled:opacity-50"
              >
                {rebookSubmitting ? (isRTL ? "جارٍ..." : "Booking...") : (isRTL ? "تأكيد" : "Confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice details modal */}
      {invoiceModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setInvoiceModal(null))}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-auto" onMouseDown={onModalPanelMouseDown} data-testid="portal-invoice-modal">
            <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between sticky top-0 bg-white">
              <div>
                <div className="font-bold text-stone-800 font-mono">#{invoiceModal.invoice_number || invoiceModal.invoice_id}</div>
                <div className="text-xs text-stone-500 mt-0.5">{(invoiceModal.created_at || "").slice(0, 16).replace("T", " ")}</div>
              </div>
              <div className="flex items-center gap-2">
                {invoiceModal.status === "cancelled" && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">{isRTL ? "ملغاة" : "VOID"}</span>}
                <button onClick={() => setInvoiceModal(null)} className="p-1.5 text-stone-400 hover:text-stone-700"><X size={16}/></button>
              </div>
            </div>
            <div className="p-5">
              <div className="space-y-1 text-sm mb-4">
                <div className="flex justify-between"><span className="text-stone-500">{isRTL ? "طريقة الدفع" : "Payment"}</span><span className="font-medium text-stone-800">{invoiceModal.payment_method}</span></div>
                {invoiceModal.cancel_reason && <div className="flex justify-between"><span className="text-stone-500">{isRTL ? "سبب الإلغاء" : "Cancel reason"}</span><span className="text-rose-600">{invoiceModal.cancel_reason}</span></div>}
              </div>
              <div className="border-t border-stone-100 pt-3 mb-3">
                <div className="text-xs text-stone-500 mb-2">{isRTL ? "البنود" : "Items"}</div>
                {(invoiceModal.items || []).map((it, i) => (
                  <div key={i} className="flex justify-between text-sm py-2 border-b border-stone-50 last:border-0">
                    <div className="min-w-0 flex-1">
                      <div className="text-stone-800 truncate">{isRTL ? (it.name_ar || it.name) : (it.name_en || it.name)}</div>
                      {it.qty > 1 && <div className="text-[10px] text-stone-400 mt-0.5">{it.unit_price} × {it.qty}</div>}
                    </div>
                    <div className="ms-3 font-mono text-stone-700">{((it.unit_price || 0) * (it.qty || 1)).toFixed(2)}</div>
                  </div>
                ))}
              </div>
              <div className="space-y-1 text-sm border-t border-stone-100 pt-3">
                <div className="flex justify-between text-stone-500"><span>{isRTL ? "المجموع" : "Subtotal"}</span><span className="text-stone-700">{Number(invoiceModal.subtotal || 0).toFixed(2)}</span></div>
                {invoiceModal.discount > 0 && <div className="flex justify-between text-stone-500"><span>{isRTL ? "خصم" : "Discount"}</span><span>-{Number(invoiceModal.discount).toFixed(2)}</span></div>}
                {invoiceModal.coupon_discount > 0 && <div className="flex justify-between"><span className="text-stone-500">{isRTL ? "كوبون" : "Coupon"} ({invoiceModal.coupon_code})</span><span className="text-amber-600">-{Number(invoiceModal.coupon_discount).toFixed(2)}</span></div>}
                {invoiceModal.loyalty_value_applied > 0 && <div className="flex justify-between"><span className="text-stone-500">{isRTL ? "نقاط مستبدلة" : "Points used"}</span><span className="text-rose-600">-{Number(invoiceModal.loyalty_value_applied).toFixed(2)}</span></div>}
                {invoiceModal.tax > 0 && <div className="flex justify-between text-stone-500"><span>{isRTL ? "ضريبة" : "VAT"}</span><span className="text-stone-700">{Number(invoiceModal.tax).toFixed(2)}</span></div>}
                <div className="flex justify-between font-bold text-lg pt-2 border-t border-stone-100"><span className="text-stone-800">{isRTL ? "الإجمالي" : "Total"}</span><span className="text-[#7A1F3D]">{Number(invoiceModal.total).toFixed(2)} {isRTL ? "ر.ق" : "QAR"}</span></div>
                {invoiceModal.loyalty_earned > 0 && (
                  <div className="flex justify-between text-xs pt-2 mt-2 border-t border-stone-100"><span className="text-stone-500 flex items-center gap-1"><Star size={11} className="text-green-600"/>{isRTL ? "نقاط مكتسبة" : "Points earned"}</span><span className="text-green-700 font-bold">+{invoiceModal.loyalty_earned}</span></div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Phase 5 — Review submission modal */}
      <ReviewModal
        open={!!reviewModalBooking}
        booking={reviewModalBooking}
        isRTL={isRTL}
        apiClient={api}
        onClose={() => setReviewModalBooking(null)}
        onSubmitted={() => { setReviewModalBooking(null); loadReviews(); }}
      />
    </div>
  );
}
